/**
 * The variable bag a render is given, and the two things the harness does to
 * it. Pure, so a component may use it freely.
 */

/** What a bag has to be to reach Keystone: a JSON object at the top. */
export type VariableBag = Record<string, unknown>;

export interface ParsedBag {
  bag: VariableBag | null;
  error: string | null;
}

/**
 * Read the textarea into a bag.
 *
 * Empty is `null` rather than `{}` — the two mean different things to nobody
 * here, but sending nothing is what exercises Keystone's own reading of an
 * absent bag, which is the more interesting of the two to be able to test.
 *
 * A non-object is refused here rather than at Keystone, which answers a list
 * or a bare string with a `VALIDATION` transport error: the same refusal, one
 * round trip earlier, against the field it is about.
 */
export function parseBag(text: string): ParsedBag {
  const trimmed = text.trim();
  if (!trimmed) {
    return { bag: null, error: null };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (cause) {
    return { bag: null, error: cause instanceof Error ? cause.message : String(cause) };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { bag: null, error: 'The bag must be a JSON object, not a list or a bare value.' };
  }
  return { bag: parsed as VariableBag, error: null };
}

/**
 * Build a bag skeleton out of the paths a version references.
 *
 * `variablePaths` is derived from the body when the version was minted, so it
 * is exactly the set a render will complain about the absence of. Typing that
 * object by hand is the slowest part of testing a render and the easiest to
 * get subtly wrong — a `applicant.firstname` for an `applicant.firstName`
 * reads as a missing variable *and* an unused one, which is a real diagnostic
 * about a typo nobody meant to make.
 */
export function skeletonFromPaths(paths: readonly string[]): VariableBag {
  const bag: VariableBag = {};
  for (const path of paths) {
    const segments = path.split('.').filter(Boolean);
    if (segments.length === 0) {
      continue;
    }
    let cursor = bag;
    segments.forEach((segment, index) => {
      if (index === segments.length - 1) {
        // Already an object here means two paths disagree about whether this
        // is a value — `applicant` beside `applicant.firstName` — which
        // Keystone refuses at write time as a conflict, so it cannot arrive
        // from a stored body. The branch is kept, because it is the half that
        // still renders something.
        if (!(segment in cursor)) {
          cursor[segment] = '';
        }
        return;
      }
      const next = cursor[segment];
      if (next === null || typeof next !== 'object' || Array.isArray(next)) {
        cursor[segment] = {};
      }
      cursor = cursor[segment] as VariableBag;
    });
  }
  return bag;
}

/** The bag as it goes in the textarea: stable key order, two-space indent. */
export function formatBag(bag: VariableBag): string {
  return JSON.stringify(bag, null, 2);
}
